import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, HeartHandshake, Rocket, ShieldCheck, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { Section, fadeInUp } from '../components/Section';
import { PUBLIC_IMAGES } from '../lib/publicImages';
import { EditorialImageCard } from '../components/EditorialImageCard';

const ESSENCE = [
  'Neuralia369 nace para convertir complejidad empresarial en una operación más limpia, más clara y más controlable.',
  'Nuestra esencia no es vender IA como moda, sino usarla como palanca real para ordenar, conectar y acelerar negocios.',
  'Pensamos en sistemas vivos: tecnología, personas, procesos y memoria trabajando bajo una misma dirección.'
];

const MISSION = [
  'Ahorrar tiempo real a empresas y equipos eliminando fricción, tareas repetitivas y desorden operativo.',
  'Construir soluciones que unifiquen captación, atención, operación, administración y dirección sin fragmentar herramientas.',
  'Dar a cada cliente una estructura útil para trabajar mejor hoy y escalar mejor mañana.'
];

const VISION = [
  'Ser el sistema operativo de referencia para empresas que quieren integrar inteligencia, memoria, automatización y criterio humano en una sola capa.',
  'Liderar un nuevo estándar de gestión empresarial donde cada área crítica del negocio pueda coordinarse, aprender y evolucionar con continuidad.',
  'Crear una infraestructura tan útil y completa que se convierta en la base natural desde la que nuestros clientes crecen, deciden y compiten.'
];

const COMMITMENTS = [
  'Cumplimiento responsable orientado a AI Act, RGPD y buenas prácticas de supervisión.',
  'Transparencia en procesos, reporting y decisiones críticas.',
  'Supervisión humana donde el impacto comercial, legal o relacional lo exige.',
  'Innovación constante para mantener la solución viva, útil y siempre alineada con negocio real.'
];

export default function NosotrosPage() {
  return (
    <>
      <SEO
        title="Nosotros | Neuralia369"
        description="Somos Neuralia369: la inteligencia que conecta, aprende y transforma con una visión humana, diferenciadora y operativa."
        path="/nosotros"
      />

      <div className="min-h-screen bg-background pt-24 pb-golden-2xl" data-testid="nosotros-page">
        <Section className="pb-golden-xl">
          <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg grid gap-golden-xl lg:grid-cols-[1.1fr_0.9fr] items-start">
            <motion.div variants={fadeInUp}>
              <p className="editorial-kicker mb-golden-md" data-testid="nosotros-kicker">Nosotros</p>
              <h1 data-testid="nosotros-title">Somos Neuralia369: la inteligencia que <span className="gradient-text">conecta, aprende y transforma</span></h1>
              <p className="mt-golden-md text-lg text-text-body max-w-3xl" data-testid="nosotros-intro">
                Nacimos con una visión clara: ayudar a las empresas a respirar, crecer y expandirse sin fricción.
                No creemos en herramientas sueltas ni en soluciones genéricas. Creemos en una capa de dirección y ejecución
                capaz de pensar, aprender, recordar y coordinar operaciones reales con criterio humano y potencia tecnológica.
              </p>
              <div className="mt-golden-lg flex flex-wrap gap-4">
                <Link to="/soluciones" className="btn-primary" data-testid="nosotros-soluciones-cta">
                  Ver cómo lo resolvemos
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/contacto" className="btn-secondary" data-testid="nosotros-contacto-cta">
                  Hablar con Neuralia369
                </Link>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-card tracing-panel p-golden-xl space-y-4" data-testid="nosotros-identity-card">
              <EditorialImageCard
                image={PUBLIC_IMAGES.about.hero}
                alt="Equipo Neuralia369 en conversación estratégica"
                kicker="Identidad"
                title="Tecnología con criterio y confianza"
                description="En Neuralia369 no solo construimos tecnología. Construimos una estructura de trabajo donde negocio, memoria, automatización y supervisión conviven para dar más solidez a cada decisión."
                testId="nosotros-hero-image"
                minHeight="min-h-[22rem]"
              />
            </motion.div>
          </div>
        </Section>

        <Section className="pt-0">
          <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg grid gap-golden-lg xl:grid-cols-2">
            {[
              { title: 'Nuestra esencia', icon: HeartHandshake, items: ESSENCE, testId: 'nosotros-esencia', image: PUBLIC_IMAGES.about.essence },
              { title: 'Nuestra misión', icon: Brain, items: MISSION, testId: 'nosotros-mision', image: PUBLIC_IMAGES.about.mission },
              { title: 'Nuestra visión', icon: Rocket, items: VISION, testId: 'nosotros-vision', image: PUBLIC_IMAGES.about.vision },
              { title: 'Nuestro compromiso', icon: ShieldCheck, items: COMMITMENTS, testId: 'nosotros-compromiso', image: PUBLIC_IMAGES.about.commitment },
            ].map(({ title, icon: Icon, items, testId, image }) => (
              <motion.article key={title} variants={fadeInUp} className="glass-card tracing-panel p-golden-xl" data-testid={testId}>
                <div className="flex items-center gap-3 mb-golden-md">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl text-text-heading font-heading font-semibold">{title}</h2>
                </div>

                <EditorialImageCard
                  image={image}
                  alt={title}
                  kicker={title}
                  title={title}
                  description={
                    title === 'Nuestra esencia'
                      ? 'La esencia define cómo pensamos: con estructura, criterio y una ambición real de transformar operación empresarial.'
                      : title === 'Nuestra misión'
                        ? 'La misión convierte intención en servicio útil: menos caos, más rendimiento y más capacidad de ejecución para cada cliente.'
                        : title === 'Nuestra visión'
                          ? 'La visión marca el horizonte: construir un sistema que se convierta en infraestructura real de crecimiento y liderazgo.'
                          : 'El compromiso sostiene la confianza: control, transparencia, supervisión y mejora constante sin perder humanidad.'
                  }
                  testId={`${testId}-image`}
                  minHeight="min-h-[17rem]"
                />

                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item} className="rounded-[20px] border border-border-subtle bg-card/35 px-4 py-4">
                      <p className="text-sm text-text-body leading-7">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.article>
            ))}
          </div>
        </Section>
      </div>
    </>
  );
}